import React from "react";
import { useIsLoggedIn } from "@dynamic-labs/sdk-react-core";
import { useDispatch, useSelector } from "react-redux";
import {
  BanIcon,
  CheckCheckIcon,
  CheckIcon,
  CircleAlertIcon,
  HourglassIcon,
  LogInIcon,
  TicketIcon,
  WalletIcon,
  SwordsIcon,
} from "lucide-react";
import {
  GetBattleByIdApiResponse as Battle,
  Position,
  Claim,
  ClaimType,
  Ticket,
} from "@dripit/types";

import PencilTeal from "@/assets/images/pencil-teal.png";
import HoldingMoney from "@/assets/images/holding-money.png";
import Star from "@/assets/svg/13-side-star.svg?react";
import UsdcStrokeIcon from "@/assets/svg/usdc-stroke.svg?react";
import TrophyUrl from "@/assets/images/trophy.png";
import {
  DottedSeparator,
  SizeInput,
  TicketsColumnKey,
  TicketsTable,
  ToggleFilter,
} from "@/components/main";
import { cn } from "@/helpers/tailwind";
import { formatBalance, formatCurrency } from "@/helpers/number";
import { authActions, RootState } from "@/store";
import { useBreakpoints, useTranslate } from "@/hooks";
import { usdcToUsd } from "@/helpers/convert";
import isNil from "lodash.isnil";
import { useProfile } from "@/api/userQueries";
import dayjs from "dayjs";
import { Button } from "@/components/ui";
import { EnhancedBattleArtist } from "@/types";

export const TosQuickAgreement: React.FC = () => (
  <p className="text-xs text-slate-400 lg:text-sm">
    * By trading, you agree to the Terms of Service
  </p>
);

const Winner: React.FC<{
  winnerArtist: EnhancedBattleArtist | undefined;
}> = ({ winnerArtist }) => (
  <div
    className={cn(
      "flex flex-col items-center justify-center gap-1",
      "overflow-hidden rounded-2xl bg-slate-900 px-3 py-4"
    )}>
    <p className="font-phonk text-3xl text-slate-900 [-webkit-text-stroke:1px_#2dd4bf]">
      Winner
    </p>
    <div
      className={cn(
        "relative size-[100px] rounded-full border border-teal-400 bg-slate-900",
        "flex translate-y-[-15px] items-center justify-center"
      )}>
      <div className="size-20 overflow-hidden rounded-full">
        {winnerArtist?.image && (
          <img
            className="object-fit size-full"
            src={winnerArtist?.image ?? ""}
            alt={winnerArtist?.name ?? ""}
          />
        )}
      </div>
      <div className="absolute bottom-[-20px] left-1/2 -translate-x-1/2">
        <Star className="size-10 stroke-teal-400" />
        <img
          src={TrophyUrl}
          alt="trophy"
          className={cn(
            "absolute left-1/2 top-1/2 size-6 -translate-x-1/2 -translate-y-1/2"
          )}
        />
      </div>
    </div>
    <p className="text-md max-w-full truncate pt-2 font-phonk">
      {winnerArtist?.name ?? "Unknown Artist"}
    </p>
  </div>
);

export const POSITION_TYPES = [
  { id: "wallet", labelKey: "common.other.wallet", Icon: WalletIcon },
  { id: "tickets", labelKey: "common.other.tickets", Icon: TicketIcon },
];

interface BuyContainerProps {
  size: number | string;
  ticketsTotalAmount: number;
  makePredictionLoading: boolean;
  addSizeLoading: boolean;
  potentialProfit: { amount: number; percentage: number };
  selectedPositionType: string;
  setSelectedPositionType: (positionType: string) => void;
  setSelectedTicketIds: (ticketIds: Ticket["tokenId"][]) => void;
  setSize: (size: number | string) => void;
}

export const BuyContainer: React.FC<BuyContainerProps> = ({
  size,
  ticketsTotalAmount,
  makePredictionLoading,
  addSizeLoading,
  potentialProfit,
  selectedPositionType,
  setSelectedPositionType,
  setSelectedTicketIds,
  setSize,
}) => {
  const t = useTranslate();
  const isLoggedIn = useIsLoggedIn();
  const { isGreaterThan } = useBreakpoints();
  const { balance } = useSelector((state: RootState) => state.wallet);
  const { data, isLoading: ticketsLoading } = useSelector(
    (state: RootState) => state.profile
  );
  const activeTickets = React.useMemo(
    () =>
      data?.tickets?.filter(
        (ticket) =>
          !ticket.used &&
          dayjs().isBefore(dayjs((ticket.expiration_date ?? 0) * 1000))
      ) ?? [],
    [data?.tickets]
  );
  const { refetch: getProfile } = useProfile({
    enabled: isLoggedIn && selectedPositionType === "tickets",
  });

  const content = {
    positionTypeToggle: (
      <ToggleFilter<string>
        className={cn(
          "[&>*]:h-8 [&>*]:w-[88px] [&_svg]:size-4",
          "lg:p-0.5 lg:[&>*]:h-6 lg:[&>*]:w-6 lg:[&_p]:hidden"
        )}
        disabled={!isLoggedIn}
        items={POSITION_TYPES}
        selectedId={selectedPositionType}
        onChange={(value) => {
          setSelectedPositionType(value);
          if (isLoggedIn && value === "tickets") {
            getProfile();
          }
        }}
      />
    ),
  };

  return (
    <div className="flex flex-col gap-2">
      <DottedSeparator className="my-2 w-full" />
      <div className="flex w-full items-center justify-between gap-2 font-geist">
        <p className="flex-grow text-sm">Amount</p>
        <p className="font-geist text-xs text-indigo-300">
          <span className="mr-2">Min:</span>
          <span>$ 1</span>
        </p>
        <div className="hidden items-center gap-2 lg:flex">
          {content.positionTypeToggle}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="lg:hidden">{content.positionTypeToggle}</div>
        <SizeInput
          size={size}
          setSize={setSize}
          hideMaxButton={
            !isGreaterThan("lg") || selectedPositionType === "tickets"
          }
          disabled={
            selectedPositionType === "tickets" ||
            makePredictionLoading ||
            addSizeLoading
          }
        />
      </div>
      {selectedPositionType === "wallet" && (
        <div className="flex flex-col gap-2">
          <div className="flex w-full items-center justify-between gap-2 text-xs text-slate-400">
            <div className="flex flex-row items-center gap-1">
              <UsdcStrokeIcon className="size-[14px] text-slate-400" />
              <span>Balance:</span>
            </div>
            <span>$ {formatBalance(Number(balance ?? 0))}</span>
          </div>
          <div className="flex w-full items-center justify-between gap-2 text-xs text-slate-400">
            <div className="flex flex-row items-center gap-1">
              <TicketIcon className="size-[14px] text-slate-400" />
              <span>Tickets:</span>
            </div>
            <span>$ {ticketsTotalAmount}</span>
          </div>
        </div>
      )}
      {selectedPositionType === "tickets" && (
        <TicketsTable
          className={cn(
            "border border-slate-500 [&_tr]:border [&_tr]:border-b-slate-500",
            "[&_button]:text-xs [&_td]:p-2 [&_td]:text-xs"
          )}
          data={activeTickets}
          columnsToInclude={[
            TicketsColumnKey.Amount,
            TicketsColumnKey.ExpirationDate,
          ]}
          loading={ticketsLoading}
          onSelectRow={(ticketIds) => setSelectedTicketIds(ticketIds)}
        />
      )}

      {/* potential profit */}
      <div
        className={cn(
          "mt-2 w-full rounded-md bg-green-400/15 p-1.5 text-center text-green-300",
          "flex items-center justify-between"
        )}>
        <span className="text-xs">Potential profit</span>
        <div className="flex items-center justify-center gap-1 text-[11px] font-semibold">
          <span>$ {potentialProfit.amount.toFixed(2)}</span>
          <span className="h-3 w-0 border border-r-green-300" />
          <span>{potentialProfit.percentage.toFixed(0)}%</span>
        </div>
      </div>
      <DottedSeparator className="my-2 w-full" />
      <div className="flex w-full items-center gap-2 text-slate-300">
        <CircleAlertIcon className="size-4" />
        <p className="text-sm">Disclaimer</p>
      </div>
      <p className="text-xs text-slate-400">
        {t("components.disclaimer_description")}
      </p>
    </div>
  );
};

interface PendingResultsProps {
  battle: Battle;
  userPositions?: Position[];
  artists?: EnhancedBattleArtist[];
}

export const PendingResults: React.FC<PendingResultsProps> = ({
  battle,
  userPositions,
  artists,
}) => (
  <div className="flex h-full w-full flex-col gap-2">
    <div
      className={cn(
        "flex w-full items-center justify-center gap-3",
        "rounded-lg bg-orange-400/15 px-3 py-2 text-orange-400"
      )}>
      <HourglassIcon className="size-4" />
      <p className="text-sm">Waiting for Results</p>
    </div>
    <DottedSeparator className="my-3 w-full" />
    <p>My positions</p>
    {userPositions?.map((position) => {
      const positionArtist =
        position?.artist ??
        battle?.battleSides?.find((side) => position.battle_side_id === side.id)
          ?.artist;

      return (
        <div
          key={position.id}
          className={cn("flex flex-col rounded-xl bg-slate-900 p-2")}>
          <div className="flex items-center justify-between gap-2">
            <div className="size-10 flex-shrink-0 overflow-hidden rounded-lg">
              <img
                className="object-fit size-full"
                src={positionArtist?.image}
                alt={positionArtist?.name}
              />
            </div>
            <p className="flex-grow truncate text-sm text-white">
              {positionArtist?.name}
            </p>
            <p className="text-xl font-semibold text-slate-300">
              {(
                artists?.find((artist) => artist.id === positionArtist?.id)
                  ?.odds ?? 0
              ).toFixed(2)}
              %
            </p>
          </div>
          <DottedSeparator className="my-2 w-full" />
          <div
            className={cn(
              "flex items-center justify-center rounded-lg p-2",
              "bg-orange-400/15 text-orange-400"
            )}>
            <p>$ {formatBalance(usdcToUsd(Number(position?.amount ?? 0)))}</p>
          </div>
        </div>
      );
    })}
    <div className="flex flex-grow flex-col justify-end">
      <TosQuickAgreement />
    </div>
  </div>
);

export const PendingResultsNoPositions: React.FC = () => (
  <div
    className={cn(
      "flex w-full flex-col items-center justify-center gap-3",
      "rounded-2xl bg-orange-400/15 px-3 py-8 text-orange-400"
    )}>
    <HourglassIcon />
    <p>Waiting for Results</p>
  </div>
);

export const CanceledNoPositions: React.FC = () => (
  <div
    className={cn(
      "flex w-full flex-col items-center justify-center gap-3",
      "rounded-2xl bg-slate-400/15 px-3 py-8 text-slate-400"
    )}>
    <BanIcon />
    <p>Canceled</p>
  </div>
);

interface CanceledProps {
  battle: Battle;
  userPositions?: Position[];
  artists?: EnhancedBattleArtist[];
  renderClaimButton: (claim?: Claim) => React.ReactNode;
}

export const Canceled: React.FC<CanceledProps> = ({
  battle,
  userPositions,
  artists,
  renderClaimButton,
}) => (
  <div className="flex h-full w-full flex-col gap-2">
    <div
      className={cn(
        "flex w-full items-center justify-center gap-3",
        "rounded-lg bg-slate-400/15 px-3 py-2 text-slate-400"
      )}>
      <BanIcon className="size-4" />
      <p className="text-sm">Canceled</p>
    </div>
    <DottedSeparator className="my-3 w-full" />
    <p>My positions</p>
    {userPositions?.map((position) => {
      const positionArtist =
        position?.artist ??
        battle?.battleSides?.find((side) => position.battle_side_id === side.id)
          ?.artist;

      return (
        <div
          key={position.id}
          className={cn(
            "flex flex-col rounded-xl bg-slate-900 p-2 opacity-50"
          )}>
          <div className="flex items-center justify-between gap-2">
            <div className="size-10 flex-shrink-0 overflow-hidden rounded-lg">
              <img
                className="object-fit size-full"
                src={positionArtist?.image}
                alt={positionArtist?.name}
              />
            </div>
            <p className="flex-grow truncate text-sm text-white">
              {positionArtist?.name}
            </p>
            <p className="text-xl font-semibold text-slate-300">
              {(
                artists?.find((artist) => artist.id === positionArtist?.id)
                  ?.odds ?? 0
              ).toFixed(2)}
              %
            </p>
          </div>
          <DottedSeparator className="my-2 w-full" />
          <div
            className={cn(
              "flex items-center justify-center gap-2 rounded-lg p-2",
              "bg-slate-400/15 text-sm text-slate-400"
            )}>
            <BanIcon className="size-4" />
            <p>Canceled</p>
            <span>|</span>
            <p className="font-semibold">
              $ {formatBalance(usdcToUsd(Number(position?.amount ?? 0)))}
            </p>
          </div>
        </div>
      );
    })}
    {battle?.claims
      ?.filter((claim) => claim?.type === ClaimType.Refund)
      ?.map((claim) => (
        <div
          key={claim.id}
          className={cn("flex flex-col rounded-xl bg-slate-900 p-2")}>
          <div className="flex items-center justify-between gap-2">
            <div className="size-10 overflow-hidden rounded-lg bg-orange-300/15">
              <img
                className="object-fit size-full"
                src={HoldingMoney}
                alt="holding money"
              />
            </div>
            <p className="flex-grow truncate text-sm text-white">Refund</p>
          </div>
          <DottedSeparator className="my-2 w-full" />
          {isNil(claim?.claimed_at) ? (
            <div className="flex items-center justify-between gap-2">
              <div
                className={cn(
                  "flex flex-grow items-center justify-center gap-2 rounded-lg p-2",
                  "bg-orange-400/15 text-sm text-orange-400"
                )}>
                <p className="font-semibold">
                  $ {formatCurrency(usdcToUsd(Number(claim?.amount ?? 0)))}
                </p>
              </div>
              {renderClaimButton(claim)}
            </div>
          ) : (
            <div
              className={cn(
                "flex w-full items-center justify-center gap-2",
                "rounded-md bg-orange-400/20 px-2 py-2 font-geist text-sm text-orange-400"
              )}>
              <CheckIcon className="size-4" />
              <span>Claimed</span>
              <span>|</span>
              <span>
                $ {formatCurrency(usdcToUsd(Number(claim?.amount ?? 0)))}
              </span>
            </div>
          )}
        </div>
      ))}

    <div className="flex flex-grow flex-col justify-end">
      <TosQuickAgreement />
    </div>
  </div>
);

interface ResolvedProps {
  battle: Battle;
  userPositions?: Position[];
  artists?: EnhancedBattleArtist[];
  winnerArtist?: EnhancedBattleArtist;
  renderClaimButton: (claim?: Claim) => React.ReactNode;
}

export const Resolved: React.FC<ResolvedProps> = ({
  battle,
  userPositions,
  artists,
  winnerArtist,
  renderClaimButton,
}) => {
  return (
    <div className="flex h-full w-full flex-col gap-2">
      <div
        className={cn(
          "flex w-full items-center justify-center gap-3",
          "rounded-lg bg-slate-400/15 px-3 py-2 text-slate-400"
        )}>
        <CheckCheckIcon className="size-4" />
        <p className="text-sm">Resolved</p>
      </div>
      <Winner winnerArtist={winnerArtist} />
      <DottedSeparator className="my-2 w-full" />
      <p>My positions</p>
      {userPositions?.map((position) => {
        const battleSide = battle?.battleSides?.find(
          (side) => position.battle_side_id === side.id
        );
        const positionArtist = position?.artist ?? battleSide?.artist;
        const claim = battle?.claims?.find(
          (claim) => claim.position_id === position?.id
        );
        const hasClaimed = !isNil(claim) && !isNil(claim?.claimed_at);

        return (
          <div
            key={position.id}
            className={cn("flex flex-col rounded-xl bg-slate-900 p-2", {
              "opacity-50": !position?.won,
            })}>
            <div className="flex items-center justify-between gap-2">
              <div className="size-10 flex-shrink-0 overflow-hidden rounded-lg">
                <img
                  className="object-fit size-full"
                  src={positionArtist?.image}
                  alt={positionArtist?.name}
                />
              </div>
              <p className="flex-grow truncate text-sm text-white">
                {positionArtist?.name}
              </p>
              <p className="text-xl font-semibold text-slate-300">
                {(
                  artists?.find((artist) => artist.id === positionArtist?.id)
                    ?.odds ?? 0
                ).toFixed(2)}
                %
              </p>
            </div>
            <DottedSeparator className="my-2 w-full" />
            {position?.won ? (
              <div className="flex items-center justify-between gap-2">
                {!hasClaimed ? (
                  <>
                    <div
                      className={cn(
                        "flex flex-grow items-center justify-center gap-2 rounded-lg p-2",
                        "bg-slate-400/15 text-sm text-slate-400"
                      )}>
                      <p className="font-semibold">
                        ${" "}
                        {formatCurrency(usdcToUsd(Number(claim?.amount ?? 0)))}
                      </p>
                    </div>
                    {renderClaimButton(claim)}
                  </>
                ) : (
                  <div
                    className={cn(
                      "flex w-full items-center justify-center gap-2",
                      "rounded-md bg-teal-400/20 px-2 py-2 font-geist text-sm text-teal-400"
                    )}>
                    <CheckIcon className="size-4" />
                    <span>Claimed</span>
                    <span>|</span>
                    <span>
                      $ {formatCurrency(usdcToUsd(Number(claim?.amount ?? 0)))}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div
                className={cn(
                  "flex items-center justify-center gap-2 rounded-lg p-2",
                  {
                    "bg-teal-400/15 text-teal-400": position?.won,
                    "bg-red-400/15 text-red-400": !position?.won,
                  }
                )}>
                <p className="font-semibold">
                  $ {formatBalance(usdcToUsd(Number(position?.amount ?? 0)))}
                </p>
              </div>
            )}
          </div>
        );
      })}
      {battle?.claims
        ?.filter((claim) => claim?.type === ClaimType.BattleMarketMaker)
        ?.map((claim) => (
          <div
            key={claim.id}
            className={cn("flex flex-col rounded-xl bg-slate-900 p-2")}>
            <div className="flex items-center justify-between gap-2">
              <div className="size-10 overflow-hidden rounded-lg">
                <img
                  className="object-fit size-full"
                  src={PencilTeal}
                  alt="pencil"
                />
              </div>
              <p className="flex-grow truncate text-sm text-white">
                Creator Reward
              </p>
            </div>
            <DottedSeparator className="my-2 w-full" />
            {isNil(claim?.claimed_at) ? (
              <div className="flex items-center justify-between gap-2">
                <div
                  className={cn(
                    "flex flex-grow items-center justify-center gap-2 rounded-lg p-2",
                    "bg-teal-400/15 text-sm text-teal-400"
                  )}>
                  <p className="font-semibold">
                    $ {formatCurrency(usdcToUsd(Number(claim?.amount ?? 0)))}
                  </p>
                </div>
                {renderClaimButton(claim)}
              </div>
            ) : (
              <div
                className={cn(
                  "flex w-full items-center justify-center gap-2",
                  "rounded-md bg-teal-400/20 px-2 py-2 font-geist text-sm text-teal-400"
                )}>
                <CheckIcon className="size-4" />
                <span>Claimed</span>
                <span>|</span>
                <span>
                  $ {formatCurrency(usdcToUsd(Number(claim?.amount ?? 0)))}
                </span>
              </div>
            )}
          </div>
        ))}
    </div>
  );
};

export const ResolvedNoPositions: React.FC<{
  winnerArtist?: EnhancedBattleArtist;
}> = ({ winnerArtist }) => (
  <div className="flex h-full w-full flex-col gap-2">
    <div
      className={cn(
        "flex w-full items-center justify-center gap-3",
        "rounded-lg bg-slate-400/15 px-3 py-2 text-slate-400"
      )}>
      <CheckCheckIcon className="size-4" />
      <p className="text-sm">Resolved</p>
    </div>
    <Winner winnerArtist={winnerArtist} />
  </div>
);

export const AuthenticateToBet: React.FC = () => {
  const dispatch = useDispatch();

  return (
    <div
      className={cn(
        "flex flex-col items-center gap-2",
        "text-center text-slate-400"
      )}>
      <div
        className={cn(
          "flex items-center justify-center rounded-full",
          "mb-3 size-[56px] bg-indigo-400/15"
        )}>
        <SwordsIcon className="size-8 stroke-1 text-indigo-400" />
      </div>
      <p>You’re just one step away!</p>
      <p>Sign in to back your favorite artist and join the action.</p>
      <DottedSeparator className="mb-5 mt-4 w-full" />
      <Button
        variant="tertiary"
        className="w-full"
        onClick={() => dispatch(authActions.toggleTriggerLogin())}>
        <LogInIcon />
        <span>Sign in</span>
      </Button>
    </div>
  );
};
